import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { IonPage, IonContent } from "@ionic/react";
import { Check, Copy, Link2 } from "lucide-react";
import { useStore } from "../../store/useStore";
import { useToast } from "../../hooks/useToast";
import { PageHeader } from "../../components/ui/PageHeader";
import { Avatar } from "../../components/ui/Avatar";
import { Button } from "../../components/ui/Button";

export default function HouseholdSettingsPage() {
  const user = useStore((s) => s.user);
  const currentHouseholdId = useStore((s) => s.currentHouseholdId);
  const households = useStore((s) => s.households);
  const userHouseholds = useStore((s) => s.userHouseholds);
  const updateHouseholdSettings = useStore((s) => s.updateHouseholdSettings);
  const showToast = useToast((s) => s.showToast);

  const currentHousehold = households.find((h) => h.uuid === currentHouseholdId);
  const isParent =
    userHouseholds.find(
      (uh) => uh.household_uuid === currentHouseholdId && uh.user_uuid === user?.id
    )?.role === "parent";

  const [name, setName] = useState(currentHousehold?.name ?? "");
  const [isSavingName, setIsSavingName] = useState(false);
  const [inviteLink, setInviteLink] = useState("");
  const [copySuccess, setCopySuccess] = useState(false);

  // Whether the user has typed in the name field since it was last synced
  const [nameDirty, setNameDirty] = useState(false);

  // Reset the field when switching households
  useEffect(() => {
    setName(currentHousehold?.name ?? "");
    setNameDirty(false);
  }, [currentHousehold?.uuid]); // eslint-disable-line react-hooks/exhaustive-deps

  // Follow remote (realtime) renames only while the user isn't editing
  useEffect(() => {
    if (!nameDirty) setName(currentHousehold?.name ?? "");
  }, [currentHousehold?.name, nameDirty]);

  const trimmedName = name.trim();
  const nameChanged = !!currentHousehold && trimmedName !== currentHousehold.name;

  const handleRename = async (e: FormEvent) => {
    e.preventDefault();
    if (!currentHouseholdId || !trimmedName || !nameChanged) return;
    setIsSavingName(true);
    const updated = await updateHouseholdSettings(currentHouseholdId, { name: trimmedName });
    setIsSavingName(false);
    if (updated) {
      setNameDirty(false);
      showToast("Household renamed", "success");
    } else {
      showToast("Couldn't rename household. Please try again.", "danger");
    }
  };

  const handleGenerateInvite = async () => {
    if (!currentHouseholdId || !currentHousehold) return;
    try {
      const fullInviteLink = `${window.location.origin}/invite?code=${currentHousehold.invite_code}`;
      setInviteLink(fullInviteLink);
    } catch (error) {
      console.error("Error generating invite link:", error);
      showToast("Failed to generate invite link", "danger");
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch {
      const textArea = document.createElement("textarea");
      textArea.value = inviteLink;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }
  };

  return (
    <IonPage>
      <PageHeader title="Household Settings" backHref="/settings" />
      <IonContent>
        <div className="max-w-lg mx-auto px-4 py-4 pb-28">
          {currentHousehold && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 px-1">
                <Avatar
                  name={currentHousehold.name}
                  seed={currentHousehold.uuid}
                  shape="square"
                  size="xl"
                />
                <h2 className="text-2xl font-extrabold tracking-tight text-foreground truncate">
                  {currentHousehold.name}
                </h2>
              </div>

              {/* Rename — parents only */}
              {isParent && (
                <form onSubmit={handleRename} className="card-surface p-4">
                  <label htmlFor="household-name" className="field-label">
                    Household name
                  </label>
                  <div className="flex gap-2">
                    <input
                      id="household-name"
                      type="text"
                      value={name}
                      onChange={(e) => {
                        setName(e.target.value);
                        setNameDirty(true);
                      }}
                      maxLength={60}
                      autoComplete="off"
                      className="input-field"
                    />
                    <Button
                      type="submit"
                      disabled={!trimmedName || !nameChanged}
                      loading={isSavingName}
                      className="min-h-[48px]"
                    >
                      Save
                    </Button>
                  </div>
                </form>
              )}

              <div className="card-surface p-4">
                <h3 className="font-bold text-foreground mb-1">
                  Invite Family Members
                </h3>
                <p className="text-muted-foreground text-sm mb-4">
                  Generate a link to invite family members to join your household.
                </p>

                {inviteLink ? (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={inviteLink}
                      readOnly
                      aria-label="Invite link"
                      className="input-field text-sm"
                      onFocus={(e) => e.currentTarget.select()}
                    />
                    <Button
                      onClick={handleCopyLink}
                      variant={copySuccess ? "soft" : "primary"}
                      leadingIcon={
                        copySuccess ? (
                          <Check className="w-4 h-4" aria-hidden="true" />
                        ) : (
                          <Copy className="w-4 h-4" aria-hidden="true" />
                        )
                      }
                      className="min-h-[48px]"
                    >
                      {copySuccess ? "Copied!" : "Copy"}
                    </Button>
                  </div>
                ) : (
                  <Button
                    variant="soft"
                    onClick={handleGenerateInvite}
                    leadingIcon={<Link2 className="w-4 h-4" aria-hidden="true" />}
                  >
                    Generate Invite Link
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}
